import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FlaskConical, Mic, MessageSquare, Check } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * AutoTestModal — 自動 E2E 情境測試
 *
 * 流程：
 *   1. 使用者可選填想測哪些情境（一行一個），留空 → AI 自動從 flow 分析挑
 *   2. 模式：voice (預設、TTS+STT 真實鏈) / text (純文字快測)
 *   3. 場景數量：1-5（影響跑多久）
 *   4. 點「開始測試」→ sendMessage 觸發 test-ivr-e2e skill
 *
 * 跟 ChatPane 的 sendMessage 同 channel：對話視窗會即時看到 AI 進度
 */
export function AutoTestModal({ open, onClose, onApply, isStreaming, avatarName, newMode = null }) {
    const { t } = useTranslation('wsTest');
    const [scenarios, setScenarios] = useState('');
    const [mode, setMode] = useState('voice');         // voice | text
    const [count, setCount] = useState(2);

    if (!open) return null;

    const isSurvey = newMode === 'survey';

    const handleStart = () => {
        if (isStreaming) return;
        const list = scenarios.split('\n').map(s => s.trim()).filter(Boolean);

        const lines = isSurvey
            ? [`請用 test-survey-e2e skill 自動測試這個問卷 AI 角色 (${avatarName || 'avatar'})。`, '']
            : [`請用 test-ivr-e2e skill 自動測試這個 AI 角色 (${avatarName || 'avatar'})。`, ''];

        if (list.length > 0) {
            lines.push(`要跑的情境(${list.length} 個):`);
            list.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
        } else if (isSurvey) {
            lines.push(`要跑 **${count}** 個受訪者情境,自己從 surveyQuestions + surveyFlow.canvasFlow 分析:`);
            lines.push('- 看每題選項 + 條件分支邊 + 隨機題組,挑出能走完不同 flow 路徑的受訪者人格');
            lines.push('- 至少一個 happy path(全程配合到結尾)');
            lines.push('- 至少一個拒訪/掛機路徑(走挽留分支)');
            lines.push('- 至少一個含「無意見/未回答」這類中性答的路徑,驗證 getNextQuestion 正常進行');
        } else {
            lines.push(`要跑 **${count}** 個情境,自己從 ivrFlowConfig 分析:`);
            lines.push('- 看 router scenarios + knowledge / apiTool node instructions');
            lines.push('- 挑能涵蓋主要 flow 路徑的代表情境(產品問答 / 留單 / 轉接 / 結束 等)');
            lines.push('- 至少一個 happy path,至少一個轉真人(如果有 transfer 節點)');
        }
        lines.push('');
        lines.push(`模式:${mode === 'voice' ? '**語音 (--tts,TTS→PCM→WS→STT→LLM→TTS 真實鏈)**' : '**純文字(無 TTS,快測)**'}`);
        lines.push('');
        if (isSurvey) {
            lines.push('每跑完一通彙報:PASS/FAIL、turns、question flow(走過哪幾題)、是否走進挽留/隨機組、是否完成所有題目,最後給總結表格。');
        } else {
            lines.push('每跑完一個情境彙報結果(PASS/FAIL、turns、flow path、命中的 KB / 工具),最後給總結表格。');
        }

        const modeLabel = mode === 'voice' ? t('autoTest.mode.voice') : t('autoTest.mode.text');
        const roleLabel = isSurvey ? t('autoTest.role.survey') : t('autoTest.role.ivr');
        const displayText = list.length > 0
            ? t('autoTest.displayText.list', { role: roleLabel, mode: modeLabel, list: list.map((s, i) => `${i + 1}. ${s}`).join('\n') })
            : t('autoTest.displayText.auto', { role: roleLabel, count, mode: modeLabel });

        onApply(lines.join('\n'), { displayText });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-3">
                        <div className="size-9 rounded-md bg-accent-50 flex items-center justify-center">
                            <FlaskConical size={18} className="text-accent-700" strokeWidth={1.7} />
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">e2e scenarios</div>
                            <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('autoTest.title')}</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500">
                        {isSurvey
                            ? t('autoTest.intro.survey')
                            : t('autoTest.intro.ivr')}
                    </div>

                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                            {t('autoTest.scenariosLabel')}
                        </label>
                        <textarea
                            value={scenarios}
                            onChange={(e) => setScenarios(e.target.value)}
                            rows={6}
                            placeholder={isSurvey
                                ? t('autoTest.scenariosPlaceholder.survey')
                                : t('autoTest.scenariosPlaceholder.ivr')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                                {t('autoTest.modeLabel')}
                            </label>
                            <div className="grid grid-cols-2 gap-1.5">
                                <ModeButton
                                    active={mode === 'voice'}
                                    onClick={() => setMode('voice')}
                                    icon={Mic}
                                    label={t('autoTest.mode.voice')}
                                    hint={t('autoTest.mode.voiceHint')}
                                />
                                <ModeButton
                                    active={mode === 'text'}
                                    onClick={() => setMode('text')}
                                    icon={MessageSquare}
                                    label={t('autoTest.mode.text')}
                                    hint={t('autoTest.mode.textHint')}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                                {t('autoTest.countLabel')}
                            </label>
                            <select
                                value={count}
                                onChange={(e) => setCount(Number(e.target.value))}
                                disabled={scenarios.trim().length > 0}
                                className="w-full text-sm px-3 py-2 rounded-md border border-cream-300 bg-white disabled:bg-cream-100 disabled:text-ink-400"
                            >
                                {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>{t('autoTest.countOption', { n })}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200 bg-cream-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleStart}
                        disabled={isStreaming}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                        <Check size={14} /> {t('autoTest.start')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModeButton({ active, onClick, icon: Icon, label, hint }) {
    return (
        <button
            onClick={onClick}
            className={[
                'flex flex-col items-start gap-0.5 px-3 py-2 rounded-md border transition',
                active
                    ? 'border-accent-500 bg-accent-50/50 ring-1 ring-accent-300'
                    : 'border-cream-200 bg-white hover:border-accent-300',
            ].join(' ')}
        >
            <div className="flex items-center gap-1.5">
                <Icon size={13} className={active ? 'text-accent-700' : 'text-ink-500'} strokeWidth={1.7} />
                <span className="text-sm font-medium text-ink-900">{label}</span>
            </div>
            <span className="text-[10px] text-ink-400 truncate w-full">{hint}</span>
        </button>
    );
}
